import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Skeleton, DashboardCardSkeleton } from "../../components/ui/Skeleton";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/Alert";
import { BarChart } from "@mui/x-charts";
import is_money from "../../helpers/is_money";
import StudentBasic from "./StudentBasic";
import { motion } from "framer-motion";
import {
  FiCreditCard, FiBook, FiBarChart2, FiHome as FiHostel,
  FiFileText, FiCreditCard as FiWallet, FiBell, FiPlus,
  FiCalendar, FiArrowRight
} from "react-icons/fi";

const Dashboard = () => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboardDataset"],
    queryFn: async () => {
      const { data } = await axiosget(`ms_dashboard/student`);
      return data;
    },
  });

  const getTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const isExamUrgent = (dateString) => {
    if (!dateString) return false;
    const examDate = new Date(dateString);
    const now = new Date();
    return Math.ceil((examDate - now) / 86400000) <= 3;
  };

  const quickActions = [
    { label: "Pay Fees", icon: FiCreditCard, to: "/app/payment", color: "bg-primary-100 text-primary-600" },
    { label: "Register", icon: FiBook, to: "/app/new-registration", color: "bg-success-100 text-success-600" },
    { label: "Results", icon: FiBarChart2, to: "/app/result", color: "bg-warning-100 text-warning-600" },
    { label: "Hostel", icon: FiHostel, to: "/app/hostel-booking", color: "bg-danger-100 text-danger-600" },
    { label: "Materials", icon: FiFileText, to: "/app/materials", color: "bg-purple-100 text-purple-600" },
    { label: "Wallet", icon: FiWallet, to: "/app/wallet-transaction", color: "bg-teal-100 text-teal-600" },
  ];

  if (isLoading) {
    return (
      <div className="animate-fade-in space-y-6">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-6">
            <DashboardCardSkeleton />
            <DashboardCardSkeleton />
          </div>
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-24 w-full" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger"><AlertTitle>Error</AlertTitle><AlertDescription>Local server error: {error.message}</AlertDescription></Alert>;
  }

  if (data?.type !== undefined) {
    return <Alert variant="danger"><AlertTitle>Error</AlertTitle><AlertDescription>{data?.message}</AlertDescription></Alert>;
  }

  if (!data) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-700 rounded-2xl p-8 text-white shadow-lg flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold mb-2">Welcome back, {data?.studentName?.split(" ")[0] || "Student"}!</h1>
          <p className="opacity-90 text-sm">{data?.programme} &bull; {data?.level} &bull; {data?.semester}</p>
        </div>
        <Badge variant="outline" size="lg" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
          {data?.currentSession} Academic Session
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Wallet Card */}
          <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl p-5 text-white shadow-lg flex items-center justify-between gap-4">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider opacity-90 mb-1">Wallet Balance</h3>
              <p className="text-2xl font-extrabold">{is_money(data?.walletBalance || 0)}</p>
            </div>
            <Link to="/app/wallet-transaction" className="bg-white/20 hover:bg-white/30 text-white px-4 py-2.5 rounded-xl font-semibold text-sm no-underline inline-flex items-center gap-1.5 backdrop-blur-sm transition-all hover:-translate-y-0.5">
              <FiPlus className="w-4 h-4" /> Add Funds
            </Link>
          </div>

          {/* Student Profile */}
          <StudentBasic />

          {/* Notifications */}
          <Card>
            <h2 className="text-lg font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-primary-500 rounded-full" />
              Notifications
              {data?.unreadNotifications > 0 && (
                <Badge variant="danger" size="sm">{data.unreadNotifications}</Badge>
              )}
            </h2>

            {data?.recentNotifications?.length > 0 ? (
              <>
                {data.recentNotifications.map((notif, i) => (
                  <div key={i} className="flex items-start gap-3 py-3 border-b border-neutral-100 last:border-0">
                    <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0">
                      <FiBell className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-neutral-800 mb-1">{notif.title || "Notification"}</h4>
                      <p className="text-xs text-neutral-500 m-0">{notif.message?.substring(0, 50)}...</p>
                    </div>
                  </div>
                ))}
                <Link to="/app/notifications" className="block text-center text-primary-600 text-xs font-semibold no-underline pt-3 mt-2 border-t border-neutral-100 hover:underline">
                  View All Notifications
                </Link>
              </>
            ) : (
              <div className="text-center py-8 text-neutral-400">
                <FiBell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm m-0">No notifications</p>
              </div>
            )}
          </Card>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Actions */}
          <div>
            <h2 className="text-lg font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-primary-500 rounded-full" />
              Quick Actions
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
              {quickActions.map((action, i) => {
                const Icon = action.icon;
                return (
                  <motion.button
                    key={i}
                    whileHover={{ y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate(action.to)}
                    className="bg-white rounded-2xl p-5 text-center shadow-sm hover:shadow-md transition-shadow border border-neutral-100 flex flex-col items-center gap-2.5 cursor-pointer"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${action.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-semibold text-neutral-700">{action.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Academic Stats */}
          <div>
            <h2 className="text-lg font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-primary-500 rounded-full" />
              Academic Performance
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card padding="sm" className="flex flex-col items-center justify-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-primary-500" />
                <div className={`w-[60px] h-[60px] rounded-full flex items-center justify-center text-white font-extrabold shadow-md ${
                  parseFloat(data?.gpa) >= 4.0 ? 'bg-gradient-to-br from-green-500 to-green-600' :
                  parseFloat(data?.gpa) >= 3.0 ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                  parseFloat(data?.gpa) >= 2.0 ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' :
                  'bg-gradient-to-br from-red-400 to-red-500'
                }`}>
                  {data?.gpa || "0.00"}
                </div>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mt-2">CGPA</p>
              </Card>

              {[
                { value: data?.totalCredits || 0, label: "Total Credits", color: "bg-teal-500" },
                { value: data?.regs || 0, label: "Registered", color: "bg-blue-500" },
                { value: data?.passed || 0, label: "Passed", color: "bg-green-500" },
              ].map((stat, i) => (
                <Card key={i} padding="sm" className="relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-1 h-full ${stat.color}`} />
                  <h1 className="text-2xl font-extrabold text-neutral-900 m-0">{stat.value}</h1>
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mt-1.5">{stat.label}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Upcoming Exams */}
            <Card>
              <h2 className="text-lg font-bold text-neutral-800 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-primary-500 rounded-full" />
                Upcoming Exams
              </h2>
              {data?.upcomingExams?.length > 0 ? (
                data.upcomingExams.map((exam, i) => {
                  const examDate = new Date(exam.date);
                  const urgent = isExamUrgent(exam.date);
                  return (
                    <div key={i} className={`flex gap-4 p-3 rounded-xl mb-2.5 last:mb-0 border-l-4 ${urgent ? 'bg-red-50 border-red-500' : 'bg-neutral-50 border-primary-500'}`}>
                      <div className="text-center min-w-[50px]">
                        <div className={`text-2xl font-extrabold leading-none ${urgent ? 'text-red-500' : 'text-neutral-800'}`}>{examDate.getDate()}</div>
                        <div className="text-[0.7rem] text-neutral-500 uppercase">{examDate.toLocaleDateString('en', { month: 'short' })}</div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-neutral-800 mb-1">{exam.course?.code || "Course"}</h4>
                        <p className="text-xs text-neutral-500 m-0">{exam.course?.title || "Exam"}</p>
                        <p className="text-xs text-neutral-500 m-0">{exam.hall?.name || "TBA"} &bull; {exam.time || "TBA"}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-neutral-400">
                  <FiCalendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm m-0">No upcoming exams</p>
                </div>
              )}
            </Card>

            {/* Recent Transactions */}
            <Card>
              <h2 className="text-lg font-bold text-neutral-800 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-primary-500 rounded-full" />
                Recent Transactions
              </h2>
              {data?.recentTransactions?.length > 0 ? (
                <>
                  {data.recentTransactions.map((trx, i) => (
                    <div key={i} className="flex justify-between items-center py-3 border-b border-neutral-100 last:border-0">
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-800 mb-1">{trx.purpose || (trx.trxtype ? "Deposit" : "Payment")}</h4>
                        <p className="text-[0.7rem] text-neutral-400 m-0">{getTimeAgo(trx.createdAt)}</p>
                      </div>
                      <span className={`font-bold text-sm ${trx.trxtype ? 'text-success-600' : 'text-danger-500'}`}>
                        {trx.trxtype ? "+" : "-"}{is_money(trx.amount)}
                      </span>
                    </div>
                  ))}
                  <Link to="/app/wallet-transaction" className="block text-center text-primary-600 text-xs font-semibold no-underline pt-3 mt-2 border-t border-neutral-100 hover:underline">
                    View All Transactions
                  </Link>
                </>
              ) : (
                <div className="text-center py-8 text-neutral-400">
                  <FiCreditCard className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm m-0">No transactions yet</p>
                </div>
              )}
            </Card>
          </div>

          {/* Result Distribution Chart */}
          <Card>
            <h2 className="text-lg font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-primary-500 rounded-full" />
              Result Distribution
            </h2>
            <BarChart
              series={[{
                data: [data?.a || 0, data?.b || 0, data?.c || 0, data?.d || 0, data?.e || 0, data?.f || 0],
                color: '#2e97cc'
              }]}
              height={250}
              xAxis={[{ data: ["A", "B", "C", "D", "E", "F"], scaleType: "band" }]}
              margin={{ top: 10, bottom: 30, left: 40, right: 10 }}
              borderRadius={8}
            />
          </Card>
        </div>
      </div>
    </motion.div>
  );
};

export default Dashboard;
